import { NavigateOptions } from 'react-router/dist/lib/context';
import { ReactNode } from 'react';
import { ConfirmModalProps, LinkButtonProps, IconButtonProps, ButtonProps } from '@hevoio/hevo-ui-toolkit';
import { GenericApiResponse, GenericKeyValueType } from '../../shared/interfaces/generics';

export enum EntityActionType {
  INLINE = 'INLINE',
  MENU = 'MENU',
  INLINE_ICON = 'INLINE_ICON',
  INLINE_LINK = 'INLINE_LINK'
}

export enum EntityActionHandlerType {
  CONFIRM = 'CONFIRM',
  NAVIGATE = 'NAVIGATE',
  CUSTOM = 'CUSTOM'
}

// Presence-only marker: the reauthorize action carries a `meta` object to signal it is available;
// no field is read (v2 reauth is version-agnostic — the former auth_provider/fortress_url are gone).
export type ReauthorizeActionMeta = Record<string, unknown>;

export interface EntityRawAction<T> {
  type: T;
  display_name: string;
  description?: string;
  is_disabled?: boolean;
  disabled_reason?: string;
  meta?: ReauthorizeActionMeta;
}

export interface EntityAction<T> {
  action: T;
  type: EntityActionType;
  actionHandlerType: EntityActionHandlerType;
  displayName: string;
  description?: string;
  icon?: string;
  iconClass?: string;
  iconSize?: number;
  disabled?: boolean;
  disabledReason?: string;
  /** Destructive action: error-coloured, and separated from the rest by a rule in the menu. */
  destructive?: boolean;
  inlineOptions?: Partial<ButtonProps>;
  inlineIconOptions?: Partial<IconButtonProps>;
  inlineLinkOptions?: Partial<LinkButtonProps>;
  routeOptions?: {
    route: string;
    options?: NavigateOptions;
  };
  className?: string;
  onClick?: () => void;
  CustomAction?: ReactNode;
  meta?: ReauthorizeActionMeta;
  trackingProps?: {
    action: string;
    properties?: GenericKeyValueType<string, unknown>;
  };
}

export type EntityActionConfirmDialogPropsValue<E> = Partial<
  Omit<ConfirmModalProps<object, object>, 'confirmationMessage'> & {
    actionHandler: (args: { entity: E; signal: AbortSignal; [key: string]: unknown }) => Promise<GenericApiResponse<unknown>>;
    successMessage: string;
    confirmationMessage?: string | ((args: { entity: E }) => string);
    postConfirmationDialogProps?: Partial<ConfirmModalProps<object, object>> | ((args: any) => Partial<ConfirmModalProps<object, object>>);
  }
>;

export type EntityActionConfirmDialogProps<A extends string | number | symbol, E> = Record<A, EntityActionConfirmDialogPropsValue<E>>;
